class CampaignHoverImage extends HTMLElement {
  constructor() {
    super();
    this.progress = 0;
    this.targetProgress = 0;
    this.animating = false;
    this.gl = null;
    this.program = null;
    this.texture = null;
    this.imageResolution = [0, 0];
  }

  connectedCallback() {
    this.img1Src = this.getAttribute('data-img1');

    if (!this.img1Src) return;

    // Create a canvas
    this.canvas = document.createElement('canvas');
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.zIndex = '1';
    this.appendChild(this.canvas);

    // Save visual fallback div
    this.fallback = this.querySelector('.campaign-gallery__fallback');
    if (this.fallback) {
      this.fallback.style.position = 'relative';
      this.fallback.style.zIndex = '0';
    }

    // Load single image
    this.loadImage(this.img1Src).then((img) => {
      this.initWebGL(img);
      this.resize();
      
      // Hide fallback once WebGL is successfully running
      if (this.fallback) {
        this.fallback.style.opacity = '0';
        this.fallback.style.transition = 'opacity 0.3s ease';
      }

      this.setupEvents();
    }).catch(err => {
      console.warn('WebGL hover transition failed to load. Falling back to CSS.', err);
      this.canvas.remove();
    });

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = src;
    });
  }

  setupEvents() {
    this.addEventListener('mouseenter', () => {
      this.targetProgress = 1.0;
      this.startAnimation();
    });

    this.addEventListener('mouseleave', () => {
      this.targetProgress = 0.0;
      this.startAnimation();
    });
  }

  startAnimation() {
    if (!this.animating) {
      this.animating = true;
      this.animate();
    }
  }

  animate() {
    if (!this.gl) return;

    // Hover in is fast (~0.4s), hover out/decay is slow (~1.0s)
    const speed = this.targetProgress > this.progress ? 0.06 : 0.03;
    const diff = this.targetProgress - this.progress;

    if (Math.abs(diff) > 0.001) {
      this.progress += diff * speed;
      this.renderWebGL();
      requestAnimationFrame(() => this.animate());
    } else {
      this.progress = this.targetProgress;
      this.renderWebGL();
      this.animating = false;
    }
  }

  initWebGL(img) {
    const gl = this.canvas.getContext('webgl') || this.canvas.getContext('experimental-webgl');
    if (!gl) throw new Error('WebGL not supported');
    this.gl = gl;

    // Shader code
    const vs = `
      attribute vec2 position;
      varying vec2 v_uv;
      void main() {
        v_uv = position * 0.5 + 0.5;
        v_uv.y = 1.0 - v_uv.y; // Flip Y axis
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const fs = `
      precision mediump float;
      varying vec2 v_uv;
      uniform sampler2D u_tex1;
      uniform float u_progress;
      uniform vec2 u_resolution;
      uniform vec2 u_tex_res1;

      vec2 hash2(vec2 p) {
        return fract(sin(vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)))) * 43758.5453);
      }

      float voronoi(vec2 x) {
        vec2 n = floor(x);
        vec2 f = fract(x);
        float m_dist = 8.0;
        for (int j = -1; j <= 1; j++) {
          for (int i = -1; i <= 1; i++) {
            vec2 g = vec2(float(i), float(j));
            vec2 o = hash2(n + g);
            o = 0.5 + 0.5 * sin(o * 6.2831);
            vec2 r = g + o - f;
            float d = dot(r, r);
            if (d < m_dist) {
              m_dist = d;
            }
          }
        }
        return sqrt(m_dist);
      }

      vec2 getCoverUv(vec2 uv, vec2 container, vec2 image) {
        float containerAspect = container.x / container.y;
        float imageAspect = image.x / image.y;
        if (containerAspect > imageAspect) {
          float scale = imageAspect / containerAspect;
          return vec2(uv.x, (uv.y - 0.5) * scale + 0.5);
        } else {
          float scale = containerAspect / imageAspect;
          return vec2((uv.x - 0.5) * scale + 0.5, uv.y);
        }
      }

      void main() {
        float p = u_progress;
        
        vec2 uv = getCoverUv(v_uv, u_resolution, u_tex_res1);
        
        // Voronoi cell calculation
        float noise = voronoi(v_uv * 10.0);
        
        // Liquid cell sweep threshold
        float threshold = smoothstep(0.0, 1.0, (p * 1.5 - noise * 0.5));
        
        // Extremely subtle cell offset during dissolve (no wobbly horizontal waves)
        vec2 offset = (noise - 0.5) * 0.005 * sin(p * 3.1415) * vec2(1.0, 1.0);
        
        // Sample color
        vec4 colorVal = texture2D(u_tex1, uv + offset);
        
        // Convert to grayscale/B&W using NTSC luminance weights
        float grayscale = dot(colorVal.rgb, vec3(0.299, 0.587, 0.114));
        vec4 bwVal = vec4(vec3(grayscale), colorVal.a);
        
        gl_FragColor = mix(bwVal, colorVal, threshold);
      }
    `;

    // Compile shaders
    const vsShader = this.compileShader(gl.VERTEX_SHADER, vs);
    const fsShader = this.compileShader(gl.FRAGMENT_SHADER, fs);

    // Create program
    const program = gl.createProgram();
    gl.attachShader(program, vsShader);
    gl.attachShader(program, fsShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error('Link shader failed: ' + gl.getProgramInfoLog(program));
    }
    this.program = program;

    // Quad geometry
    const vertices = new Float32Array([
      -1, -1,
       1, -1,
      -1,  1,
      -1,  1,
       1, -1,
       1,  1
    ]);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    const position = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    // Setup single texture
    this.texture = this.createTexture(img, 0);
    this.imageResolution = [img.width, img.height];
  }

  compileShader(type, src) {
    const gl = this.gl;
    const shader = gl.createShader(type);
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error('Compile shader error: ' + log);
    }
    return shader;
  }

  createTexture(image, unit) {
    const gl = this.gl;
    const texture = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, texture);

    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    return texture;
  }

  resize() {
    if (!this.canvas || !this.gl) return;
    const displayWidth = this.canvas.clientWidth;
    const displayHeight = this.canvas.clientHeight;

    if (this.canvas.width !== displayWidth || this.canvas.height !== displayHeight) {
      this.canvas.width = displayWidth;
      this.canvas.height = displayHeight;
      this.gl.viewport(0, 0, displayWidth, displayHeight);
      this.renderWebGL();
    }
  }

  renderWebGL() {
    const gl = this.gl;
    if (!gl || !this.program) return;

    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.useProgram(this.program);

    // Uniforms
    gl.uniform1f(gl.getUniformLocation(this.program, 'u_progress'), this.progress);
    gl.uniform2f(gl.getUniformLocation(this.program, 'u_resolution'), this.canvas.width, this.canvas.height);

    gl.uniform2f(gl.getUniformLocation(this.program, 'u_tex_res1'), this.imageResolution[0], this.imageResolution[1]);

    gl.uniform1i(gl.getUniformLocation(this.program, 'u_tex1'), 0);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }
}

if (!customElements.get('campaign-hover-image')) {
  customElements.define('campaign-hover-image', CampaignHoverImage);
}
